<script setup>
import { ref, watch, nextTick, onBeforeUnmount } from 'vue'
import UiModal from '@/Components/UI/UiModal.vue'
import UiButton from '@/Components/UI/UiButton.vue'

const props = defineProps({
  open:  { type: Boolean, required: true },
  title: { type: String,  default: 'Signature' },
})
const emit = defineEmits(['close', 'save'])

const canvas = ref(null)
const canvasWrap = ref(null)
const hasSigned = ref(false)
let ctx = null
let drawing = false
let lastX = 0
let lastY = 0
let resizeHandler = null

function configureCtx() {
  if (!ctx) return
  ctx.strokeStyle = '#1a1a1a'
  ctx.lineWidth = 2.5
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
}

function sizeCanvas() {
  if (!canvasWrap.value || !canvas.value) return
  canvas.value.width = canvasWrap.value.clientWidth
  canvas.value.height = canvasWrap.value.clientHeight
  configureCtx()
}

function getPos(e) {
  const rect = canvas.value.getBoundingClientRect()
  const src = e.touches ? e.touches[0] : e
  return { x: src.clientX - rect.left, y: src.clientY - rect.top }
}

const startDraw = (e) => {
  e.preventDefault()
  drawing = true
  const { x, y } = getPos(e)
  lastX = x; lastY = y
}
const draw = (e) => {
  e.preventDefault()
  if (!drawing) return
  const { x, y } = getPos(e)
  ctx.beginPath()
  ctx.moveTo(lastX, lastY)
  ctx.lineTo(x, y)
  ctx.stroke()
  lastX = x; lastY = y
  hasSigned.value = true
}
const stopDraw = () => { drawing = false }

function clearPad() {
  if (!ctx || !canvas.value) return
  ctx.clearRect(0, 0, canvas.value.width, canvas.value.height)
  hasSigned.value = false
}

async function init() {
  await nextTick()
  if (!canvas.value) return
  ctx = canvas.value.getContext('2d')
  configureCtx()
  sizeCanvas()
  resizeHandler = () => sizeCanvas()
  window.addEventListener('resize', resizeHandler)
}

function teardown() {
  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler)
    resizeHandler = null
  }
  ctx = null
  drawing = false
  hasSigned.value = false
}

watch(() => props.open, (v) => {
  if (v) {
    init()
  } else {
    teardown()
  }
})

onBeforeUnmount(teardown)

function dataUrlToBlob(dataUrl) {
  const [meta, b64] = dataUrl.split(',')
  const type = (meta.match(/data:(.*?);base64/) || [])[1] || 'image/png'
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return new Blob([bytes], { type })
}

function confirmSign() {
  if (!hasSigned.value || !canvas.value) return
  const dataUrl = canvas.value.toDataURL('image/png')
  const blob = dataUrlToBlob(dataUrl)
  emit('save', { dataUrl, blob })
}
</script>

<template>
  <UiModal
    :open="open"
    :title="title"
    size="md"
    @close="emit('close')"
  >
    <div class="space-y-3">
      <p class="text-sm text-[color:var(--inkSoft)]">
        Draw the signature using mouse or finger. It will appear on the Lessor / Agent line of every agreement PDF.
      </p>
      <div
        ref="canvasWrap"
        class="min-h-[200px] border-2 border-dashed border-[color:var(--line)] rounded-[var(--radius-md)] overflow-hidden bg-[color:var(--surfaceAlt)]"
      >
        <canvas
          ref="canvas"
          class="block w-full h-full touch-none cursor-crosshair"
          @mousedown="startDraw"
          @mousemove="draw"
          @mouseup="stopDraw"
          @mouseleave="stopDraw"
          @touchstart="startDraw"
          @touchmove="draw"
          @touchend="stopDraw"
        />
      </div>
    </div>
    <template #footer>
      <UiButton
        type="button"
        variant="ghost"
        @click="clearPad"
      >
        Clear
      </UiButton>
      <UiButton
        type="button"
        variant="ghost"
        @click="emit('close')"
      >
        Cancel
      </UiButton>
      <UiButton
        type="button"
        variant="primary"
        :disabled="!hasSigned"
        @click="confirmSign"
      >
        Save
      </UiButton>
    </template>
  </UiModal>
</template>
